import { NgFor } from '@angular/common';
import { Component, OnDestroy, inject } from '@angular/core';

import { FormArray, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatLabel } from '@angular/material/form-field';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import {
  AbstractDialogResultData,
  AbstractFormDialog,
  AbstractFormDialogData,
  FormDialogComponent,
} from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Cycle, DayOfWeekEnum, WDOsettingEnum, WDOsettings } from '../../models/cycle.model';

import { SettingComponent } from '../setting/setting.component';

export interface WDOsettingsDialogInputData extends AbstractFormDialogData {
  cycle: Cycle;
  settings: WDOsettings;
}

export interface WDOsettingsDialogResultData {
  settings: WDOsettings;
}

@Component({
  selector: 'app-wdo-settings',
  templateUrl: './wdo-settings.component.html',
  imports: [
    FormDialogComponent,
    ReactiveFormsModule,
    MatLabel,
    MatRadioGroup,
    MatRadioButton,
    NgFor,
    SettingComponent,
    TranslatePipe,
  ],
})
export class WDOsettingsComponent
  extends AbstractFormDialog<WDOsettingsDialogResultData>
  implements OnDestroy
{
  data = inject<WDOsettingsDialogInputData>(MAT_DIALOG_DATA);
  protected mdDialogRef =
    inject<
      MatDialogRef<WDOsettingsComponent, AbstractDialogResultData<WDOsettingsDialogResultData>>
    >(MatDialogRef);
  private readonly locale = inject(LocaleService);

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    super();
    this.data.title = this.locale.translate('wdo-settings.title');

    this.wdoSettingsArray.forEach((_) => {
      this.wdoSettings().push(new FormControl(WDOsettingEnum.Off));
    });

    if (!!this.data.cycle && !!this.data.cycle.observationSettings) {
      this.setWDOSettings(this.data.cycle.observationSettings);
    } else {
      this.setWDOSettings(this.data.settings);
    }

    this.formGroup.controls.generalSetting.valueChanges
      .pipe(takeUntil(this.unsubscribeSignal.asObservable()))
      .subscribe((setting) => {
        this.wdoSettings().controls.forEach((control: FormControl) => control.setValue(setting));
      });

    this.formGroup.controls.wdoSettings.valueChanges
      .pipe(takeUntil(this.unsubscribeSignal.asObservable()))
      .subscribe((settingsArray: string[]) => {
        this.determineGeneralSetting(settingsArray);
      });
  }
  wdoSettingsArray: string[] = [
    'mortality',
    'feedIntake',
    'waterIntake',
    'indoorTemperature',
    'outdoorTemperature',
    'indoorHumidity',
    'outdoorHumidity',
    'weight',
    'litterQuality',
  ];

  formGroup = new FormGroup({
    generalSetting: new FormControl(''),
    wdoSettings: new FormArray([]),
  });
  unsubscribeSignal: Subject<void> = new Subject<void>();

  private setWDOSettings(wdoSettings: WDOsettings) {
    const wdoSettingsArray: string[] = Object.values(wdoSettings).filter(
      (setting) =>
        setting === WDOsettingEnum.PerDay ||
        setting === WDOsettingEnum.PerWeek ||
        setting === WDOsettingEnum.Off,
    );

    if (WDOsettingsComponent.isAllSame(wdoSettingsArray)) {
      this.wdoSettings().controls.forEach((control: FormControl) =>
        control.setValue(wdoSettingsArray[0] ?? DayOfWeekEnum.Monday),
      );
      this.formGroup.controls.generalSetting.setValue(wdoSettingsArray[0], {
        emitEvent: false,
        onlySelf: true,
      });
    } else {
      this.wdoSettings().controls.forEach((control: FormControl, i: number) => {
        control.setValue(wdoSettingsArray[i]);
      });
      this.formGroup.controls.generalSetting.setValue('', { emitEvent: false, onlySelf: true });
    }
  }

  private static isAllSame(settingsArray: string[]): boolean {
    return settingsArray.filter((v, _, a) => v === a[0]).length === settingsArray.length;
  }

  ngOnDestroy() {
    this.unsubscribeSignal.next();
    this.unsubscribeSignal.unsubscribe();
  }

  wdoSettings(): FormArray {
    return this.formGroup.get('wdoSettings') as FormArray;
  }

  // if all settings are set to same value (PerDay or PerWeek) then the generalSetting can be set accordingly
  private determineGeneralSetting(settingsArray: string[]): void {
    if (WDOsettingsComponent.isAllSame(settingsArray)) {
      this.formGroup.controls.generalSetting.patchValue(settingsArray[0], {
        emitEvent: false,
        onlySelf: true,
      });
    } else {
      this.formGroup.controls.generalSetting.patchValue('', { emitEvent: false, onlySelf: true });
    }
  }

  save() {
    this.confirm({
      settings: {
        mortality: <WDOsettingEnum>(<string[]>this.wdoSettings().value)[0],
        feedIntake: <WDOsettingEnum>(<string[]>this.wdoSettings().value)[1],
        waterIntake: <WDOsettingEnum>(<string[]>this.wdoSettings().value)[2],
        indoorTemperature: <WDOsettingEnum>(<string[]>this.wdoSettings().value)[3],
        outdoorTemperature: <WDOsettingEnum>(<string[]>this.wdoSettings().value)[4],
        indoorHumidity: <WDOsettingEnum>(<string[]>this.wdoSettings().value)[5],
        outdoorHumidity: <WDOsettingEnum>(<string[]>this.wdoSettings().value)[6],
        weight: <WDOsettingEnum>(<string[]>this.wdoSettings().value)[7],
        litterQuality: <WDOsettingEnum>(<string[]>this.wdoSettings().value)[8],
      },
    });
  }
}
